import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, addDoc, getDocs, doc, updateDoc,
  deleteDoc, getDoc, writeBatch, setDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Accordion, AccordionSummary,
  AccordionDetails, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, ListItemSecondaryAction,
  Divider, Select, MenuItem, FormControl, InputLabel, Tooltip, Avatar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PreviewIcon from '@mui/icons-material/Preview';
import LinkIcon from '@mui/icons-material/Link';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CampaignIcon from '@mui/icons-material/Campaign';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import DomainIcon from '@mui/icons-material/Domain';
import { useNavigate } from 'react-router-dom';
import { deptCode, deptLabel } from '../utils/depts';
import { isUserAdmin } from '../utils/permissions';
import { authFetch } from '../utils/authFetch';

// Sektioner som marknadskonsulter har åtkomst till
const CONSULTANT_ACCESS = [
  { label: 'LinkedIn-verkstad', path: '/marketing/linkedin', allowed: true },
  { label: 'Mailsignatur', path: '/marketing/signature', allowed: true },
  { label: 'CRM – Företag', path: '/crm/companies', allowed: true },
  { label: 'CRM – Leads', path: '/crm/leads', allowed: true },
  { label: 'CRM – Pling-klang', path: '/crm/pling-klang', allowed: true },
  { label: 'Projekt & Anbud', path: '/projects', allowed: false },
  { label: 'HR & IUP', path: '/hr', allowed: false },
  { label: 'Medarbetarregister', path: '/employees', allowed: false },
  { label: 'Adminstration', path: '/admin', allowed: false },
];

const ROLES = [
  { value: 'superadmin',       label: 'Superadmin' },
  { value: 'vd',               label: 'VD' },
  { value: 'ao_ansvarig',      label: 'AO-ansvarig' },
  { value: 'marknadsansvarig', label: 'Marknadsansvarig' },
  { value: 'marknadskonsult',  label: 'Marknadskonsult' },
  { value: 'gruppledare',      label: 'Gruppledare' },
  { value: 'medarbetare',      label: 'Medarbetare' },
];

const roleColors = {
  superadmin: 'error',
  vd: 'secondary',
  ao_ansvarig: 'warning',
  gruppledare: 'success',
  medarbetare: 'default',
};

export default function Admin() {
  const { currentUser, userProfile } = useAuth();
  // Bara superadmin delar ut admin; admin delar ut profilredaktör (speglas i firestore.rules)
  const canGrantAdmin = userProfile?.role === 'superadmin';
  const canGrantProfileEditor = isUserAdmin(userProfile);
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [dialog, setDialog] = useState({
    open: false, type: '', parentId: '', editId: '', value: '', code: ''
  });
  const [memberDialog, setMemberDialog] = useState({
    open: false, departmentId: '', searchEmail: '',
    searchResult: null, inviteEmail: ''
  });
  const [inviteLink, setInviteLink] = useState('');
  const [cvDialog, setCvDialog] = useState({
    open: false, user: null, profile: null
  });
  const [gdprDialog, setGdprDialog] = useState({
    open: false, user: null, confirmName: '', deleting: false
  });
  const [filterDept, setFilterDept] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterGL, setFilterGL] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  // Externa konsulter
  const [consultants, setConsultants] = useState([]);
  const [consultantsLoading, setConsultantsLoading] = useState(false);
  const [newConsultantEmail, setNewConsultantEmail] = useState('');
  const [newConsultantNote, setNewConsultantNote] = useState('');
  const [consultantSaving, setConsultantSaving] = useState(false);
  const [myCompanyId, setMyCompanyId] = useState('');
  const [myName, setMyName] = useState('');
  const [emailSending, setEmailSending] = useState({}); // { email: true/false }
  // Auto-provisionering via domän
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [domainInput, setDomainInput] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  useEffect(() => { loadAll(); loadConsultants(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
    const userData = userSnap.exists() ? userSnap.data() : {};
    const role = userData.role || '';
    const deptId = userData.departmentId || '';
    const companyId = userData.companyId || '';

    setIsSuperadmin(role === 'superadmin' || role === 'vd');

    const [compSnap, deptSnap, userListSnap] = await Promise.all([
      getDocs(collection(db, 'companies')),
      getDocs(collection(db, 'departments')),
      getDocs(collection(db, 'users')),
    ]);

    let allCompanies = compSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    let allDepts = deptSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    let allUsers = userListSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Filtrera baserat på roll
    if (role === 'superadmin' || role === 'vd') {
      // Ser allt
    } else if (role === 'ao_ansvarig' || role === 'gruppledare' || userData.isAdmin) {
      // Ser bara sitt eget AO och företag
      allDepts = allDepts.filter(d => d.id === deptId);
      allCompanies = allCompanies.filter(c => c.id === companyId);
      allUsers = allUsers.filter(u => u.departmentId === deptId);
    } else {
      allCompanies = [];
      allDepts = [];
      allUsers = [];
    }

    setCompanies(allCompanies);
    setDepartments(allDepts);
    setUsers(allUsers);
    setLoading(false);
  }

  async function loadConsultants() {
    setConsultantsLoading(true);
    try {
      // Hämta inbjudningar
      const snap = await getDocs(collection(db, 'invitedConsultants'));
      const invites = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Kolla vilka som har loggat in (finns i users-kollektionen)
      const allUsersSnap = await getDocs(collection(db, 'users'));
      const allUsers = allUsersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const joinedEmails = new Set(allUsers.filter(u => u.role === 'marknadskonsult').map(u => u.email?.toLowerCase()));
      // Hämta inloggad users companyId
      const meSnap = await getDoc(doc(db, 'users', currentUser.uid));
      if (meSnap.exists()) {
        const companyId = meSnap.data().companyId || '';
        setMyCompanyId(companyId);
        const d = meSnap.data();
        setMyName(`${d.firstName || d.name || ''}`.trim());
        if (companyId) {
          const compSnap2 = await getDoc(doc(db, 'companies', companyId));
          if (compSnap2.exists()) setAllowedDomains(compSnap2.data().allowedDomains || []);
        }
      }
      setConsultants(invites.map(inv => ({
        ...inv,
        hasJoined: joinedEmails.has(inv.email?.toLowerCase()),
      })));
    } catch { /* ignorera */ } finally {
      setConsultantsLoading(false);
    }
  }

  async function handleInviteConsultant() {
    const email = newConsultantEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setConsultantSaving(true);
    try {
      await setDoc(doc(db, 'invitedConsultants', email), {
        email,
        role: 'marknadskonsult',
        companyId: myCompanyId,
        note: newConsultantNote.trim(),
        addedBy: currentUser.uid,
        addedAt: new Date(),
      });
      setNewConsultantEmail('');
      setNewConsultantNote('');
      setSaved(`${email} inbjuden som extern konsult!`);
      setTimeout(() => setSaved(''), 3000);
      loadConsultants();
    } catch (err) {
      setError('Kunde inte bjuda in: ' + err.message);
    } finally {
      setConsultantSaving(false);
    }
  }

  async function handleSendInviteEmail(consultant) {
    setEmailSending(prev => ({ ...prev, [consultant.email]: true }));
    try {
      const fn = httpsCallable(functions, 'sendConsultantInvite');
      await fn({
        email: consultant.email,
        note: consultant.note || '',
        appUrl: window.location.origin,
        invitedByName: myName,
      });
      setSaved(`Inbjudningsmail skickat till ${consultant.email}!`);
      setTimeout(() => setSaved(''), 4000);
    } catch (err) {
      setError('Kunde inte skicka mail: ' + (err.message || 'Okänt fel'));
    } finally {
      setEmailSending(prev => ({ ...prev, [consultant.email]: false }));
    }
  }

  async function handleRemoveConsultant(email) {
    if (!window.confirm(`Ta bort inbjudan för ${email}?\n\nOm personen redan loggat in påverkas inte deras konto – hantera det under Användare.`)) return;
    await deleteDoc(doc(db, 'invitedConsultants', email.toLowerCase()));
    setSaved(`Inbjudan för ${email} borttagen.`);
    setTimeout(() => setSaved(''), 3000);
    loadConsultants();
  }

  function addDomain() {
    const clean = domainInput.trim().toLowerCase().replace(/^@/, '').replace(/^https?:\/\//, '');
    if (!clean || allowedDomains.find(d => d.domain === clean)) return;
    setAllowedDomains(prev => [...prev, { domain: clean, defaultRole: 'medarbetare', defaultDepartmentId: '' }]);
    setDomainInput('');
  }

  function removeDomain(domain) {
    setAllowedDomains(prev => prev.filter(d => d.domain !== domain));
  }

  function updateDomain(domain, field, value) {
    setAllowedDomains(prev => prev.map(d => d.domain === domain ? { ...d, [field]: value } : d));
  }

  async function saveDomains() {
    if (!myCompanyId) return;
    setDomainSaving(true);
    try {
      await updateDoc(doc(db, 'companies', myCompanyId), { allowedDomains });
      setSaved('Domäner sparade!');
      setTimeout(() => setSaved(''), 3000);
    } catch (err) {
      setError('Kunde inte spara domäner: ' + err.message);
    } finally {
      setDomainSaving(false);
    }
  }

  function openDialog(type, parentId, editId, value, code) {
    setDialog({
      open: true, type,
      parentId: parentId || '',
      editId: editId || '',
      value: value || '',
      code: code || '',
    });
  }

  async function handleSave() {
    const { type, parentId, editId, value, code } = dialog;
    if (!value.trim()) return;
    const offerCode = (code || '').trim().toUpperCase() || null;
    if (editId) {
      const colName = type === 'company' ? 'companies' : 'departments';
      const update = { name: value };
      if (type === 'department') update.offerCode = offerCode;
      await updateDoc(doc(db, colName, editId), update);
    } else {
      if (type === 'company') {
        // Bara superadmin
        await addDoc(collection(db, 'companies'), {
          name: value, createdBy: currentUser.uid, createdAt: new Date()
        });
      } else if (type === 'department') {
        // Bara superadmin
        await addDoc(collection(db, 'departments'), {
          name: value, offerCode, companyId: parentId, createdAt: new Date()
        });
      }
    }
    setSaved('Sparat!');
    setTimeout(() => setSaved(''), 2000);
    setDialog({ open: false, type: '', parentId: '', editId: '', value: '', code: '' });
    loadAll();
  }

  async function handleDelete(type, id) {
    if (!isSuperadmin) return;
    const colName = type === 'company' ? 'companies' : 'departments';
    await deleteDoc(doc(db, colName, id));
    loadAll();
  }

  async function handleRoleChange(userId, newRole) {
    // Kan aldrig ändra sin egen roll
    if (userId === currentUser.uid) return;
    // Kan aldrig ändra rollen på en superadmin eller VD (om man inte är superadmin)
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.role === 'superadmin') return;
    if (targetUser?.role === 'vd' && !isSuperadmin) return;
    // Bara superadmin kan tilldela superadmin- eller VD-rollen
    if ((newRole === 'superadmin' || newRole === 'vd') && !isSuperadmin) return;
    await updateDoc(doc(db, 'users', userId), { role: newRole });
    setSaved('Roll uppdaterad!');
    setTimeout(() => setSaved(''), 2000);
    loadAll();
  }

  async function handlePermissionChange(userId, field, value) {
    await updateDoc(doc(db, 'users', userId), { [field]: value });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
    setSaved('Behörighet uppdaterad!');
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleGruppledarChange(userId, gruppledarId) {
    await updateDoc(doc(db, 'users', userId), { gruppledarId });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, gruppledarId } : u));
    setSaved('Gruppledare kopplad!');
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleViewCV(user) {
    const snap = await getDoc(doc(db, 'profiles', user.id));
    const profile = snap.exists() ? snap.data() : null;
    setCvDialog({ open: true, user, profile });
  }

  function getSearchResults() {
    const q = memberDialog.searchEmail.trim().toLowerCase();
    if (q.length < 2) return [];
    return users.filter(u =>
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    );
  }

  async function handleLinkUser(user) {
    const { departmentId } = memberDialog;
    const dept = departments.find(d => d.id === departmentId);
    await updateDoc(doc(db, 'users', user.id), {
      departmentId,
      companyId: dept ? dept.companyId : ''
    });
    setSaved((user.name || user.email) + ' kopplad!');
    setTimeout(() => setSaved(''), 3000);
    setMemberDialog({
      open: false, departmentId: '', searchEmail: '',
      searchResult: null, inviteEmail: ''
    });
    setInviteLink('');
    loadAll();
  }

  async function handleInviteByEmail(departmentId) {
    const email = memberDialog.inviteEmail.trim();
    if (!email) return;
    const dept = departments.find(d => d.id === departmentId);
    // Generera token precis som länk-flödet
    const token = Math.random().toString(36).substring(2, 12) +
      Math.random().toString(36).substring(2, 12);
    await addDoc(collection(db, 'invitations'), {
      token, departmentId,
      companyId: dept?.companyId || '',
      invitedBy: currentUser.uid,
      invitedByEmail: email,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      status: 'pending', type: 'email'
    });
    const link = window.location.origin + '/join/' + token;
    try {
      await authFetch('/.netlify/functions/employee-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          inviteLink: link,
          departmentName: dept?.name || '',
          invitedByName: myName,
        }),
      });
      setSaved('Inbjudan skickad till ' + email + '!');
      setTimeout(() => setSaved(''), 4000);
      setMemberDialog(prev => ({ ...prev, inviteEmail: '' }));
    } catch {
      setSaved('Länk skapad men mail misslyckades. Kopiera länken manuellt.');
      setInviteLink(link);
    }
  }

  async function handleGenerateLink(departmentId) {
    const dept = departments.find(d => d.id === departmentId);
    const token = Math.random().toString(36).substring(2, 12) +
      Math.random().toString(36).substring(2, 12);
    await addDoc(collection(db, 'invitations'), {
      token, departmentId,
      companyId: dept?.companyId || '',
      invitedBy: currentUser.uid,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      status: 'pending', type: 'link'
    });
    const link = window.location.origin + '/join/' + token;
    setInviteLink(link);
  }

  async function handleUnlinkUser(userId) {
    await updateDoc(doc(db, 'users', userId), {
      departmentId: '', companyId: ''
    });
    loadAll();
  }

  async function handleGDPRDelete() {
    const user = gdprDialog.user;
    if (!user) return;
    setGdprDialog(prev => ({ ...prev, deleting: true }));
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'profiles', user.id));
      batch.delete(doc(db, 'users', user.id));

      const variantsSnap = await getDocs(collection(db, 'cvVariants'));
      variantsSnap.docs
        .filter(d => d.data().userId === user.id)
        .forEach(d => batch.delete(d.ref));

      const tenderMembersSnap = await getDocs(
        collection(db, 'tenderMembers'));
      tenderMembersSnap.docs
        .filter(d => d.data().userId === user.id)
        .forEach(d => batch.delete(d.ref));

      const tendersSnap = await getDocs(collection(db, 'tenders'));
      tendersSnap.docs
        .filter(d => d.data().createdBy === user.id)
        .forEach(d => batch.update(d.ref, { createdBy: '[borttagen]' }));

      await batch.commit();

      await addDoc(collection(db, 'gdprDeletions'), {
        deletedUserId: user.id,
        deletedUserEmail: user.email,
        deletedUserName: user.name || '',
        deletedBy: currentUser.uid,
        deletedAt: new Date(),
        reason: 'Admin GDPR-radering'
      });

      setSaved('All data for ' + (user.name || user.email) +
        ' har raderats.');
      setTimeout(() => setSaved(''), 5000);
      setGdprDialog({
        open: false, user: null, confirmName: '', deleting: false
      });
      loadAll();
    } catch (err) {
      console.error(err);
      setError('Raderingen misslyckades. Försök igen.');
      setGdprDialog(prev => ({ ...prev, deleting: false }));
    }
  }

  function getDeptMembers(deptId) {
    return users.filter(u => u.departmentId === deptId);
  }

  function getDeptManager(deptId) {
    return users.find(u =>
      (u.role === 'ao_ansvarig' || u.isDepartmentManager) &&
      (u.departmentId === deptId || u.managedDepartmentIds?.includes(deptId))
    );
  }


  const labels = { company: 'Företag', department: 'Affärsområde' };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ mx: -0.5 }} />
          <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.primary' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Administration
          </Typography>
          {isSuperadmin && (
            <Chip label="Superadmin" color="error" size="small" />
          )}
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}
            onClose={() => setError('')}>{error}</Alert>
        )}

        {/* Info om begransad behorighet */}
        {!isSuperadmin && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<LockIcon />}>
            <Typography variant="body2">
              Du ser bara medarbetare och inställningar för ditt
              affärsområde. Kontakta en superadmin för att skapa
              nya företag eller affärsområden.
            </Typography>
          </Alert>
        )}


        {/* Feedback-hantering — superadmin-länk */}
        {isSuperadmin && (
          <Paper
            sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }, transition: 'background 0.15s' }}
            onClick={() => navigate('/feedback-admin')}
          >
            <RateReviewIcon sx={{ color: '#EC407A', fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Feedback-hantering</Typography>
              <Typography variant="caption" color="text.secondary">
                Hantera och besvara inkomna önskemål och felrapporter
              </Typography>
            </Box>
            <ArrowBackIcon sx={{ color: 'text.disabled', transform: 'rotate(180deg)' }} />
          </Paper>
        )}

        {/* GDPR-info */}
        <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#f3f4ff',
          border: '1px solid #c5cae9' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <GppMaybeIcon sx={{ color: '#3949ab', mt: 0.3,
              flexShrink: 0 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold"
                sx={{ color: '#3949ab' }}>
                GDPR – Personuppgiftshantering
              </Typography>
              <Typography variant="body2" sx={{ color: '#3949ab',
                mt: 0.5 }}>
                LagbasN behandlar personuppgifter i enlighet med GDPR.
                Använd sköldikonen vid varje användare för att permanent
                radera all persondata (rätten att bli glömd).
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Alla användare */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center',
            gap: 1, mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Användare och roller
            </Typography>
            <Chip label="GDPR" size="small"
              sx={{ bgcolor: '#e8eaf6', color: '#3949ab',
                fontSize: '0.65rem' }} />
          </Box>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 2 }}>
            {isSuperadmin
              ? 'Alla användare i systemet.'
              : 'Medarbetare i ditt affärsområde.'}
            {' '}Använd sköldikonen för GDPR-radering.
          </Typography>

          {/* Filterrad */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Sök namn / e-post…"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              sx={{ minWidth: 180, flex: '1 1 180px',
                '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
            />
            <FormControl size="small" sx={{ minWidth: 150,
              '& .MuiInputBase-input': { fontSize: '0.78rem' },
              '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}>
              <InputLabel>Affärsområde</InputLabel>
              <Select value={filterDept} label="Affärsområde"
                onChange={e => setFilterDept(e.target.value)}>
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>Alla</em></MenuItem>
                {departments.map(d => (
                  <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.78rem' }}>{deptLabel(d)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140,
              '& .MuiInputBase-input': { fontSize: '0.78rem' },
              '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}>
              <InputLabel>Roll</InputLabel>
              <Select value={filterRole} label="Roll"
                onChange={e => setFilterRole(e.target.value)}>
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>Alla</em></MenuItem>
                {ROLES.map(r => (
                  <MenuItem key={r.value} value={r.value} sx={{ fontSize: '0.78rem' }}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150,
              '& .MuiInputBase-input': { fontSize: '0.78rem' },
              '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}>
              <InputLabel>Gruppledare</InputLabel>
              <Select value={filterGL} label="Gruppledare"
                onChange={e => setFilterGL(e.target.value)}>
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>Alla</em></MenuItem>
                {users
                  .filter(u => u.role === 'gruppledare' || u.role === 'ao_ansvarig')
                  .map(gl => (
                    <MenuItem key={gl.id} value={gl.id} sx={{ fontSize: '0.78rem' }}>
                      {gl.name || gl.email}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {(filterSearch || filterDept || filterRole || filterGL) && (
              <Button size="small" variant="text"
                onClick={() => { setFilterSearch(''); setFilterDept(''); setFilterRole(''); setFilterGL(''); }}
                sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Rensa
              </Button>
            )}
          </Box>

          <Box>
            {users
              .filter(user => {
                if (filterSearch) {
                  const q = filterSearch.toLowerCase();
                  if (!(user.name?.toLowerCase().includes(q) || user.email?.toLowerCase().includes(q))) return false;
                }
                if (filterDept && user.departmentId !== filterDept) return false;
                if (filterRole && (user.role || 'medarbetare') !== filterRole) return false;
                if (filterGL && user.gruppledarId !== filterGL) return false;
                return true;
              })
              .map(user => {
              const isLockedRole = user.role === 'superadmin' || user.id === currentUser.uid;
              const PERMISSIONS = [
                { field: 'isAdmin', label: 'Admin' },
                { field: 'isAnbudsansvarig', label: 'Anbudsansvarig' },
                { field: 'canManageMarketing', label: 'Marknadsansvarig' },
                { field: 'isHRansvarig', label: 'HR-ansvarig' },
                { field: 'isDepartmentManager', label: 'AO-ansvarig' },
                { field: 'canEditAllProfiles', label: 'Profilredaktör',
                  tooltip: 'Profilredaktör – kan redigera alla konsultprofiler' },
              ];
              const canToggle = field =>
                field === 'isAdmin' ? canGrantAdmin
                  : field === 'canEditAllProfiles' ? canGrantProfileEditor
                  : isSuperadmin;
              const primaryDept = departments.find(d => d.id === user.departmentId);
              const primaryDeptName = primaryDept?.name;
              return (
                <Box key={user.id} sx={{
                  py: 1.5, borderBottom: '1px solid',
                  borderColor: 'divider',
                }}>
                  {/* Rad 1: namn + roll + knappar */}
                  <Box sx={{ display: 'flex', alignItems: 'center',
                    gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Namn/e-post */}
                    <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {user.name || user.email}
                        </Typography>
                        {primaryDept && (
                          <Tooltip title={primaryDeptName}>
                            <Chip size="small" label={deptCode(primaryDept)}
                              sx={{ height: 18, fontSize: '0.65rem',
                                bgcolor: '#f0f0f0', color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                        {user.autoProvisioned && (
                          <Chip label="Auto" size="small"
                            sx={{ height: 16, fontSize: '0.6rem' }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>

                    {/* Roll */}
                    {isLockedRole ? (
                      <Chip size="small"
                        label={ROLES.find(r => r.value === (user.role || 'medarbetare'))?.label}
                        color={roleColors[user.role || 'medarbetare']}
                      />
                    ) : (
                      <>
                        <FormControl size="small" sx={{ minWidth: 140,
                          '& .MuiInputBase-input': { fontSize: '0.78rem' },
                          '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}>
                          <InputLabel>Roll</InputLabel>
                          <Select
                            value={user.role || 'medarbetare'}
                            label="Roll"
                            onChange={e => handleRoleChange(user.id, e.target.value)}>
                            {ROLES
                              .filter(r => isSuperadmin || r.value !== 'superadmin')
                              .map(r => (
                                <MenuItem key={r.value} value={r.value}
                                  sx={{ fontSize: '0.78rem' }}>{r.label}</MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        {(user.role === 'medarbetare' || !user.role) && (
                          <FormControl size="small" sx={{ minWidth: 150,
                            '& .MuiInputBase-input': { fontSize: '0.78rem' },
                            '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}>
                            <InputLabel>Gruppledare</InputLabel>
                            <Select
                              value={user.gruppledarId || ''}
                              label="Gruppledare"
                              onChange={e => handleGruppledarChange(user.id, e.target.value)}>
                              <MenuItem value="" sx={{ fontSize: '0.78rem' }}><em>Ingen</em></MenuItem>
                              {users
                                .filter(u => (u.role === 'gruppledare' || u.role === 'ao_ansvarig') &&
                                  u.departmentId === user.departmentId)
                                .map(gl => (
                                  <MenuItem key={gl.id} value={gl.id}
                                    sx={{ fontSize: '0.78rem' }}>
                                    {gl.name || gl.email}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        )}
                      </>
                    )}

                    {/* Åtgärdsknappar */}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5,
                      alignItems: 'center', flexShrink: 0 }}>
                      <IconButton size="small" title="Visa CV"
                        onClick={() => handleViewCV(user)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <Button size="small" variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={() => navigate('/cv-preview/' + user.id)}>
                        Profil
                      </Button>
                      {user.id !== currentUser.uid && (
                        <Tooltip title="GDPR – Radera all persondata">
                          <IconButton size="small"
                            onClick={() => setGdprDialog({
                              open: true, user, confirmName: '', deleting: false
                            })}
                            sx={{ color: '#c62828',
                              '&:hover': { bgcolor: '#ffebee' } }}>
                            <GppMaybeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {/* Rad 2: behörighetschips */}
                  {(isSuperadmin || canGrantProfileEditor) && (
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1,
                      flexWrap: 'wrap', alignItems: 'center', pl: 0.5 }}>
                      {PERMISSIONS.map(({ field, label, tooltip }) => {
                        // Anbudsansvariga är profilredaktörer via rollen – visas ikryssad och låst
                        const viaRole = field === 'canEditAllProfiles' && user.isAnbudsansvarig === true;
                        const active = viaRole || !!user[field];
                        const editable = canToggle(field) && !viaRole;
                        return (
                          <Tooltip key={field}
                            title={viaRole ? 'Ingår i Anbudsansvarig' : (tooltip || '')}>
                            <Chip
                              size="small"
                              label={label}
                              icon={viaRole ? <LockIcon sx={{ fontSize: '0.8rem !important', color: '#fff !important' }} /> : undefined}
                              clickable={editable}
                              onClick={editable ? () => handlePermissionChange(
                                user.id, field, !user[field]
                              ) : undefined}
                              variant={active ? 'filled' : 'outlined'}
                              sx={{
                                fontSize: '0.7rem',
                                ...(active
                                  ? { bgcolor: '#FF6B00', color: '#fff',
                                      '&:hover': { bgcolor: editable ? '#e65c00' : '#FF6B00' } }
                                  : { color: 'text.secondary' }),
                                ...(!editable && { opacity: viaRole ? 0.85 : 0.6, cursor: 'default' }),
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                      {/* Primärt AO-namn */}
                      {user.isDepartmentManager && primaryDeptName && (
                        <Chip size="small" label={'Primärt: ' + deptCode(primaryDept)}
                          sx={{ fontSize: '0.7rem', bgcolor: '#fff3e0',
                            color: '#e65100', border: '1px solid #ffcc80' }}
                        />
                      )}
                      {/* Extra AO-dropdown */}
                      {isSuperadmin && user.isDepartmentManager && (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <Select
                            multiple
                            displayEmpty
                            value={user.managedDepartmentIds || []}
                            onChange={e => handlePermissionChange(
                              user.id, 'managedDepartmentIds', e.target.value
                            )}
                            renderValue={selected =>
                              selected.length === 0
                                ? <em style={{ fontSize: '0.7rem' }}>
                                    + Ytterligare AO
                                  </em>
                                : selected.map(id =>
                                    deptCode(departments.find(d => d.id === id)) || id
                                  ).join(', ')
                            }
                            sx={{ fontSize: '0.75rem', height: 28 }}
                          >
                            {departments
                              .filter(d => d.id !== user.departmentId)
                              .map(d => (
                                <MenuItem key={d.id} value={d.id}
                                  sx={{ fontSize: '0.8rem' }}>
                                  {deptLabel(d)}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
            {users.filter(user => {
              if (filterSearch) {
                const q = filterSearch.toLowerCase();
                if (!(user.name?.toLowerCase().includes(q) || user.email?.toLowerCase().includes(q))) return false;
              }
              if (filterDept && user.departmentId !== filterDept) return false;
              if (filterRole && (user.role || 'medarbetare') !== filterRole) return false;
              if (filterGL && user.gruppledarId !== filterGL) return false;
              return true;
            }).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {users.length === 0 ? 'Inga användare.' : 'Inga användare matchar filtret.'}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Företag & Affärsområden */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              <Typography variant="h6" fontWeight="bold">
                Företag och Affärsområden
              </Typography>
            </Box>
            {isSuperadmin && (
              <Button startIcon={<AddIcon />} variant="contained"
                size="small"
                onClick={() => openDialog('company')}>
                Lägg till företag
              </Button>
            )}
          </Box>

          {!isSuperadmin && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<LockIcon />}>
              <Typography variant="caption">
                Bara superadmin kan skapa och ta bort företag
                och affärsområden.
              </Typography>
            </Alert>
          )}

          {companies.map(company => (
            <Accordion key={company.id} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center',
                  gap: 1, width: '100%' }}>
                  <Typography fontWeight="bold" sx={{ flex: 1 }}>
                    {company.name}
                  </Typography>
                  <Chip size="small"
                    label={departments.filter(d =>
                      d.companyId === company.id).length +
                      ' affärsområden'} />
                  {isSuperadmin && (
                    <>
                      <IconButton size="small"
                        onClick={e => {
                          e.stopPropagation();
                          openDialog('company', '',
                            company.id, company.name);
                        }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error"
                        onClick={e => {
                          e.stopPropagation();
                          handleDelete('company', company.id);
                        }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>

                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2"
                    color="text.secondary">
                    Affärsområden
                  </Typography>
                  {isSuperadmin && (
                    <Button startIcon={<AddIcon />} size="small"
                      variant="outlined"
                      onClick={() => openDialog('department',
                        company.id)}>
                      Lägg till affärsområde
                    </Button>
                  )}
                </Box>

                {departments.filter(d =>
                  d.companyId === company.id).map(dept => (
                  <Accordion key={dept.id} defaultExpanded sx={{ mb: 1, ml: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex',
                        alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography component="span" fontWeight="bold" sx={{ mr: 1 }}>
                            {dept.offerCode || ''}
                          </Typography>
                          <Typography component="span">
                            {dept.name}
                          </Typography>
                        </Box>
                        {(() => {
                          const mgr = getDeptManager(dept.id);
                          return mgr ? (
                            <Chip size="small"
                              label={`Ansvarig: ${mgr.name || mgr.email}`}
                              sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontSize: '0.65rem' }} />
                          ) : null;
                        })()}
                        <Chip size="small"
                          label={getDeptMembers(dept.id).length +
                            ' medarbetare'} />
                        {isSuperadmin && (
                          <>
                            <IconButton size="small"
                              onClick={e => {
                                e.stopPropagation();
                                openDialog('department', '',
                                  dept.id, dept.name, dept.offerCode);
                              }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error"
                              onClick={e => {
                                e.stopPropagation();
                                handleDelete('department', dept.id);
                              }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2"
                          color="text.secondary">
                          Medarbetare
                        </Typography>
                        <Button startIcon={<PersonAddIcon />}
                          size="small" variant="outlined"
                          onClick={() => {
                            setInviteLink('');
                            setMemberDialog({
                              open: true,
                              departmentId: dept.id,
                              searchEmail: '',
                              searchResult: null,
                              inviteEmail: ''
                            });
                          }}>
                          Lägg till / Bjud in
                        </Button>
                      </Box>
                      <List dense>
                        {getDeptMembers(dept.id).map(member => (
                          <React.Fragment key={member.id}>
                            <ListItem>
                              <ListItemText
                                primary={member.name || member.email}
                                secondary={member.email}
                              />
                              <ListItemSecondaryAction>
                                <IconButton size="small" color="primary"
                                  onClick={() =>
                                    navigate('/cv-preview/' + member.id)}>
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title="Koppla bort (tar ej bort data)">
                                  <IconButton size="small" color="error"
                                    onClick={() =>
                                      handleUnlinkUser(member.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                            <Divider />
                          </React.Fragment>
                        ))}
                        {getDeptMembers(dept.id).length === 0 && (
                          <ListItem>
                            <ListItemText
                              secondary="Inga medarbetare kopplade" />
                          </ListItem>
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        {/* Okopplade användare – bara superadmin */}
        {isSuperadmin && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Okopplade användare
            </Typography>
            <List dense>
              {users.filter(u => !u.departmentId).map(user => (
                <React.Fragment key={user.id}>
                  <ListItem>
                    <ListItemText
                      primary={user.name || user.email}
                      secondary={user.email}
                    />
                    <Chip size="small" label="Ej kopplad"
                      color="warning" sx={{ mr: 1 }} />
                    <IconButton size="small" color="primary"
                      onClick={() =>
                        navigate('/cv-preview/' + user.id)}>
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {users.filter(u => !u.departmentId).length === 0 && (
                <ListItem>
                  <ListItemText
                    secondary="Alla användare är kopplade!" />
                </ListItem>
              )}
            </List>
          </Paper>
        )}

        {/* ── Externa konsulter (marknadskonsulter) ── */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <CampaignIcon sx={{ color: '#FF6B00' }} />
            <Typography variant="h6" fontWeight="bold">Externa konsulter</Typography>
            <Chip label="Marknadskonsulter" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontSize: '0.65rem' }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Bjud in externa konsulter med Google-inloggning. De får tillgång till de sektioner som visas nedan.
          </Typography>

          {/* Behörighetsöversikt */}
          <Box sx={{ mb: 2.5, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #eee' }}>
            <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block', color: '#555' }}>
              Konsultens åtkomst
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {CONSULTANT_ACCESS.map(s => (
                <Chip
                  key={s.path}
                  label={s.label}
                  size="small"
                  icon={s.allowed
                    ? <CheckCircleIcon sx={{ fontSize: '0.8rem !important', color: '#2E7D32 !important' }} />
                    : <LockIcon sx={{ fontSize: '0.8rem !important', color: '#999 !important' }} />
                  }
                  sx={{
                    fontSize: '0.7rem',
                    bgcolor: s.allowed ? '#E8F5E9' : '#F5F5F5',
                    color: s.allowed ? '#2E7D32' : '#9e9e9e',
                    border: `1px solid ${s.allowed ? '#A5D6A7' : '#e0e0e0'}`,
                    '& .MuiChip-icon': { ml: '6px' },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Bjud in */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label="E-post (Gmail eller Google Workspace)"
              value={newConsultantEmail}
              onChange={e => setNewConsultantEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleInviteConsultant(); }}
              size="small"
              placeholder="konsult@externtforetag.com"
              sx={{ flex: '1 1 220px' }}
              InputProps={{ startAdornment: <GoogleIcon sx={{ fontSize: '1rem', color: '#888', mr: 0.75 }} /> }}
            />
            <TextField
              label="Anteckning (valfritt)"
              value={newConsultantNote}
              onChange={e => setNewConsultantNote(e.target.value)}
              size="small"
              placeholder="T.ex. Byrå X, ansvarig för LinkedIn-kampanj"
              sx={{ flex: '2 1 260px' }}
            />
            <Button
              variant="contained"
              startIcon={consultantSaving ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
              onClick={handleInviteConsultant}
              disabled={!newConsultantEmail.trim() || consultantSaving}
              sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, whiteSpace: 'nowrap' }}
            >
              Bjud in
            </Button>
            <Button
              variant="outlined"
              startIcon={consultantSaving ? <CircularProgress size={14} /> : <MailOutlineIcon />}
              onClick={async () => {
                await handleInviteConsultant();
                const email = newConsultantEmail.trim().toLowerCase();
                if (email) {
                  const fn = httpsCallable(functions, 'sendConsultantInvite');
                  fn({ email, note: newConsultantNote.trim(), appUrl: window.location.origin, invitedByName: myName })
                    .then(() => { setSaved(`Inbjudningsmail skickat till ${email}!`); setTimeout(() => setSaved(''), 4000); })
                    .catch(err => setError('Mail misslyckades: ' + err.message));
                }
              }}
              disabled={!newConsultantEmail.trim() || consultantSaving}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Bjud in & skicka mail
            </Button>
          </Box>

          {/* Lista */}
          {consultantsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Laddar…</Typography>
            </Box>
          ) : consultants.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Inga externa konsulter inbjudna ännu.
            </Typography>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {consultants.length} inbjudna — {consultants.filter(c => c.hasJoined).length} har loggat in
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {consultants.map(c => (
                  <Box key={c.id} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.25, borderRadius: 1.5, border: '1px solid #e8e8e8',
                    bgcolor: c.hasJoined ? '#F1F8E9' : '#FAFAFA',
                  }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: c.hasJoined ? '#558B2F' : '#bdbdbd' }}>
                      <GoogleIcon sx={{ fontSize: '1rem' }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                          {c.email}
                        </Typography>
                        {c.hasJoined ? (
                          <Chip label="Inloggad" size="small"
                            icon={<CheckCircleIcon sx={{ fontSize: '0.75rem !important' }} />}
                            sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#DCEDC8', color: '#33691E', '& .MuiChip-icon': { color: '#33691E', ml: '5px' } }}
                          />
                        ) : (
                          <Chip label="Ej loggat in" size="small"
                            icon={<ScheduleIcon sx={{ fontSize: '0.75rem !important' }} />}
                            sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#FFF8E1', color: '#F57F17', '& .MuiChip-icon': { color: '#F57F17', ml: '5px' } }}
                          />
                        )}
                      </Box>
                      {c.note && <Typography variant="caption" color="text.secondary">{c.note}</Typography>}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Inbjuden {c.addedAt?.toDate ? c.addedAt.toDate().toLocaleDateString('sv-SE') : '–'}
                        {' · Roll: '}<strong>Marknadskonsult</strong>{' · Åtkomst: Marknad + CRM'}
                      </Typography>
                    </Box>
                    <Tooltip title="Skicka inbjudningsmail">
                      <IconButton size="small" onClick={() => handleSendInviteEmail(c)} disabled={emailSending[c.email]}
                        sx={{ color: '#0a66c2', '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' } }}>
                        {emailSending[c.email] ? <CircularProgress size={16} /> : <MailOutlineIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ta bort inbjudan">
                      <IconButton size="small" onClick={() => handleRemoveConsultant(c.email)} sx={{ color: '#bdbdbd', '&:hover': { color: '#C62828' } }}>
                        <DeleteIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Paper>

        {/* AUTO-PROVISIONERING */}
        {myCompanyId && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DomainIcon sx={{ color: '#FF6B00' }} />
                <Typography variant="h6" fontWeight="bold">
                  Auto-provisionering via e-postdomän
                </Typography>
              </Box>
              <Button
                variant="contained" size="small"
                startIcon={domainSaving ? <CircularProgress size={14} color="inherit" /> : null}
                onClick={saveDomains} disabled={domainSaving}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' } }}
              >
                {domainSaving ? 'Sparar...' : 'Spara'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Användare som loggar in med en godkänd domän får automatiskt tillgång utan inbjudningslänk.
              Ange domänen utan @-tecken, t.ex. <strong>tikab.com</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {allowedDomains.map((d) => (
              <Paper key={d.domain} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={'@' + d.domain} sx={{ bgcolor: '#1a1a1a', color: 'white', fontWeight: 'bold', fontSize: '0.85rem' }} />
                    <Chip label="Aktiv" size="small" color="success" />
                  </Box>
                  <IconButton size="small" color="error" onClick={() => removeDomain(d.domain)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Standardroll</InputLabel>
                    <Select value={d.defaultRole} label="Standardroll"
                      onChange={e => updateDomain(d.domain, 'defaultRole', e.target.value)}>
                      <MenuItem value="medarbetare">Medarbetare</MenuItem>
                      <MenuItem value="anbudsansvarig">Anbudsansvarig</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Standardaffärsområde</InputLabel>
                    <Select value={d.defaultDepartmentId || ''} label="Standardaffärsområde"
                      onChange={e => updateDomain(d.domain, 'defaultDepartmentId', e.target.value)}>
                      <MenuItem value="">Inget (tilldelas manuellt)</MenuItem>
                      {departments.filter(dept => dept.companyId === myCompanyId || !dept.companyId).map(dept => (
                        <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            ))}

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <TextField
                size="small" fullWidth
                value={domainInput}
                placeholder="t.ex. tikab.com"
                label="Lägg till domän"
                onChange={e => setDomainInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addDomain(); }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
              />
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addDomain} disabled={!domainInput.trim()}>
                Lägg till
              </Button>
            </Box>
          </Paper>
        )}

        {/* API-KOPPLINGAR */}
        {isSuperadmin && (
          <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LinkIcon sx={{ color: '#FF6B00' }} />
              <Typography variant="h6" fontWeight="bold">API-kopplingar</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Nycklar och hemligheter hanteras på respektive plattform — se kolumnen "Var" nedan.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {[
              { group: 'AI', keys: [
                { name: 'ANTHROPIC_API_KEY', where: 'Firebase Secret Manager', desc: 'Claude-modeller — alla AI-anrop via Cloud Function proxy' },
              ]},
              { group: 'CRM — Företagsdata', keys: [
                { name: 'BOLAGSAPI_KEY', where: 'Netlify env', desc: 'BolagsAPI — hämtar namn, adress, SNI, F-skatt m.m. via org.nr i CRM' },
              ]},
              { group: 'CRM — LinkedIn-berikening', keys: [
                { name: 'ENRICHLAYER_KEY', where: 'Netlify env', desc: 'Enrich Layer — hämtar LinkedIn-data för företag och kontaktpersoner (namn, roll, utbildning, språk, bolagsbeskrivning m.m.)' },
              ]},
              { group: 'CRM — Adress & Karta', keys: [
                { name: '(ingen nyckel)', where: 'Ingen konfiguration', desc: 'OpenStreetMap / Nominatim — adressautokomplettering och kartnål. Gratis, ingen API-nyckel krävs.' },
              ]},
              { group: 'E-post', keys: [
                { name: 'RESEND_API_KEY', where: 'Netlify env', desc: 'Resend — transaktionsmail (inbjudningar, ny-användarnotis)' },
                { name: 'RESEND_FROM_EMAIL', where: 'Netlify env', desc: 'Avsändaradress för Resend-mail, t.ex. noreply@lagbasn.se' },
              ]},
              { group: 'Firebase', keys: [
                { name: 'REACT_APP_FIREBASE_API_KEY', where: 'Netlify env', desc: 'Firebase Web SDK — autentisering, Firestore, Storage' },
                { name: 'REACT_APP_FIREBASE_VAPID_KEY', where: 'Netlify env', desc: 'Web Push VAPID-nyckel — pushnotiser till superadmins (FCM)' },
              ]},
              { group: 'Kollektivtrafik', keys: [
                { name: 'TRAFIKLAB_RESROBOT_KEY', where: 'Netlify env', desc: 'ResRobot v2.1 — avgångstider SL, Västtrafik m.fl.' },
                { name: 'TRAFIKLAB_STOPS_KEY', where: 'Netlify env', desc: 'ResRobot location.name — hållplatssökning' },
                { name: 'TRAFIKVERKET_API_KEY', where: 'Netlify env', desc: 'Trafikverket — tåg, spår och förseningar' },
              ]},
              { group: 'Bilder', keys: [
                { name: 'UNSPLASH_ACCESS_KEY', where: 'Netlify env', desc: 'Unsplash — slumpade bakgrundsbilder i PitchN-presentationer' },
              ]},
              { group: 'Konsultförmedling', keys: [
                { name: 'UPGRADED_API_KEY', where: 'Netlify env', desc: 'Upgraded — hämtar konsultförfrågningar från Upgraded-portalen' },
              ]},
            ].map(({ group, keys }) => (
              <Box key={group} sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.75 }}>
                  {group}
                </Typography>
                {keys.map(k => (
                  <Box key={k.name} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: '#f0f0f0', px: 0.8, py: 0.2, borderRadius: 1, whiteSpace: 'nowrap' }}>
                      {k.name}
                    </Typography>
                    <Chip label={k.where} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8eaf6', color: '#3949ab' }} />
                    <Typography variant="caption" color="text.secondary">{k.desc}</Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Paper>
        )}

        {/* GDPR-information */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #c5cae9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center',
            gap: 1, mb: 1.5 }}>
            <GppMaybeIcon sx={{ color: '#3949ab' }} />
            <Typography variant="h6" fontWeight="bold"
              sx={{ color: '#3949ab' }}>
              GDPR – Registrerades rättigheter
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column',
            gap: 1.5 }}>
            {[
              {
                title: 'Vilken data lagrar vi?',
                text: 'Namn, e-post, profilbild, yrkestitel och CV-information. Data lagras i Google Firebase inom EU (europe-north1, Finland).'
              },
              {
                title: 'Rätten att bli glömd',
                text: 'En medarbetare kan begära radering av all data. Utför raderingen via GDPR-knappen (sköldikon) i användarlistan. Raderingen loggas för spårbarhet.'
              },
              {
                title: 'Ändamål',
                text: 'Personuppgifter behandlas för intern konsultmatchning, CV-hantering och anbudsarbete. Data delas inte med tredje part.'
              },
              {
                title: 'Personuppgiftsansvarig',
                text: 'Er organisation är personuppgiftsansvarig. Tikab Strukturmekanik AB är personuppgiftsbiträde som tillhandahåller systemet.'
              },
            ].map((item, i) => (
              <Box key={i}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Dialog skapa/redigera */}
      <Dialog open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.editId ? 'Redigera' : 'Lägg till'}{' '}
          {labels[dialog.type]}
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Namn"
            value={dialog.value}
            onChange={e => setDialog({ ...dialog, value: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            sx={{ mt: 1 }} />
          {dialog.type === 'department' && (
            <TextField fullWidth label="Kod (ex BTM)"
              value={dialog.code}
              onChange={e => setDialog({ ...dialog, code: e.target.value })}
              inputProps={{ maxLength: 6, style: { textTransform: 'uppercase' } }}
              helperText="2–4 bokstäver. Används som chip och i offertnummer."
              sx={{ mt: 2 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() =>
            setDialog({ ...dialog, open: false, code: '' })}>
            Avbryt
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog koppla/bjud in */}
      <Dialog open={memberDialog.open}
        onClose={() => {
          setMemberDialog({ ...memberDialog, open: false });
          setInviteLink('');
        }}
        maxWidth="sm" fullWidth>
        <DialogTitle>Lägg till medarbetare</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" fontWeight="bold"
            sx={{ mt: 1, mb: 1 }}>
            Koppla befintlig användare
          </Typography>
          <TextField fullWidth size="small" label="Sök på namn eller e-post"
            value={memberDialog.searchEmail}
            onChange={e => setMemberDialog(prev => ({
              ...prev, searchEmail: e.target.value, searchResult: null
            }))}
            helperText="Minst 2 tecken"
            sx={{ mb: 1 }}
          />
          {getSearchResults().map(u => (
            <Alert key={u.id} severity="success" sx={{ mb: 1 }}
              action={
                <Button size="small" onClick={() => handleLinkUser(u)}>
                  Koppla
                </Button>
              }>
              {u.name || u.email}{u.name && u.email ? ` – ${u.email}` : ''}
            </Alert>
          ))}
          {memberDialog.searchEmail.trim().length >= 2 && getSearchResults().length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Ingen användare hittades. Använd inbjudningslänk nedan.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold"
            sx={{ mb: 1 }}>
            Inbjudningslänk
          </Typography>
          <Typography variant="body2" color="text.secondary"
            gutterBottom>
            Giltig i 7 dagar
          </Typography>
          <Button variant="outlined" fullWidth startIcon={<LinkIcon />}
            onClick={() =>
              handleGenerateLink(memberDialog.departmentId)}
            sx={{ mb: 1 }}>
            Generera inbjudningslänk
          </Button>
          {inviteLink && (
            <Box sx={{ p: 2, bgcolor: 'action.hover',
              borderRadius: 1, mb: 2 }}>
              <Typography variant="caption"
                sx={{ wordBreak: 'break-all', display: 'block' }}>
                {inviteLink}
              </Typography>
              <Button size="small" fullWidth variant="contained"
                sx={{ mt: 1 }}
                onClick={() => {
                  navigator.clipboard.writeText(inviteLink);
                  setSaved('Länk kopierad!');
                  setTimeout(() => setSaved(''), 2000);
                }}>
                Kopiera länk
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Bjud in via e-post
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField fullWidth size="small" label="E-postadress"
              value={memberDialog.inviteEmail}
              onChange={e => setMemberDialog(prev => ({ ...prev, inviteEmail: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') handleInviteByEmail(memberDialog.departmentId); }}
              placeholder="namn@foretag.se"
            />
            <Button variant="contained"
              disabled={!memberDialog.inviteEmail.trim()}
              onClick={() => handleInviteByEmail(memberDialog.departmentId)}
              sx={{ bgcolor: '#1a1a1a', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#333' } }}>
              Bjud in
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMemberDialog({ ...memberDialog, open: false });
            setInviteLink('');
          }}>
            Stäng
          </Button>
        </DialogActions>
      </Dialog>

      {/* CV-dialog */}
      <Dialog open={cvDialog.open}
        onClose={() => setCvDialog({ ...cvDialog, open: false })}
        maxWidth="md" fullWidth>
        <DialogTitle>
          CV – {cvDialog.user?.name || cvDialog.user?.email}
        </DialogTitle>
        <DialogContent dividers>
          {!cvDialog.profile ? (
            <Alert severity="info">
              Denna medarbetare har inte fyllt i sitt CV ännu.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column',
              gap: 3 }}>
              {cvDialog.profile.ingress && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold"
                    gutterBottom>
                    Ingress
                  </Typography>
                  <Typography variant="body2">
                    {cvDialog.profile.ingress}
                  </Typography>
                </Box>
              )}
              {cvDialog.profile.competencies?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold"
                    gutterBottom>
                    Kompetensområden
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap',
                    gap: 1 }}>
                    {cvDialog.profile.competencies.map((c, i) => (
                      <Chip key={i} label={c} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              {cvDialog.profile.assignments?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold"
                    gutterBottom>
                    Uppdrag
                  </Typography>
                  {cvDialog.profile.assignments.map((a, i) => (
                    <Paper key={i} variant="outlined"
                      sx={{ p: 2, mb: 1 }}>
                      <Typography fontWeight="bold">{a.name}</Typography>
                      <Typography variant="caption"
                        color="text.secondary">
                        {[a.period, a.client, a.role]
                          .filter(Boolean).join(' | ')}
                      </Typography>
                      {a.personalDescription && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {a.personalDescription}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() =>
            setCvDialog({ ...cvDialog, open: false })}>
            Stäng
          </Button>
          <Button variant="contained" startIcon={<PreviewIcon />}
            onClick={() => {
              setCvDialog({ ...cvDialog, open: false });
              navigate('/cv-preview/' + cvDialog.user?.id);
            }}>
            Visa konsultprofil
          </Button>
        </DialogActions>
      </Dialog>

      {/* GDPR-raderingsdialog */}
      <Dialog open={gdprDialog.open}
        onClose={() => !gdprDialog.deleting && setGdprDialog({
          open: false, user: null, confirmName: '', deleting: false
        })}
        maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center',
          gap: 1 }}>
          <GppMaybeIcon sx={{ color: '#c62828' }} />
          GDPR – Radera all persondata
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              OBS! Permanent och kan inte ångras.
            </Typography>
          </Alert>
          <Typography variant="body2" gutterBottom>
            Du är på väg att radera all data för:
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold"
            sx={{ mb: 2 }}>
            {gdprDialog.user?.name || gdprDialog.user?.email}
            {gdprDialog.user?.email && gdprDialog.user?.name && (
              <Typography component="span" variant="body2"
                color="text.secondary" sx={{ ml: 1 }}>
                ({gdprDialog.user.email})
              </Typography>
            )}
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2 }}>
            {[
              'Användarkonto och inloggning',
              'Konsultprofil och CV',
              'Alla CV-varianter',
              'Kopplingen till affärsområde',
              'Medverkan i anbudsteam',
            ].map((item, i) => (
              <li key={i}>
                <Typography variant="body2">{item}</Typography>
              </li>
            ))}
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Raderingen loggas för GDPR-spårbarhet.
            </Typography>
          </Alert>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Bekräfta genom att skriva personens namn:
          </Typography>
          <TextField fullWidth autoFocus size="small"
            placeholder={gdprDialog.user?.name ||
              gdprDialog.user?.email || ''}
            value={gdprDialog.confirmName}
            onChange={e => setGdprDialog(prev => ({
              ...prev, confirmName: e.target.value
            }))}
            helperText={'Skriv: ' + (gdprDialog.user?.name ||
              gdprDialog.user?.email || '')}
            error={gdprDialog.confirmName.length > 0 &&
              gdprDialog.confirmName !== (gdprDialog.user?.name ||
                gdprDialog.user?.email)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={gdprDialog.deleting}
            onClick={() => setGdprDialog({
              open: false, user: null, confirmName: '',
              deleting: false
            })}>
            Avbryt
          </Button>
          <Button variant="contained" color="error"
            disabled={
              gdprDialog.deleting ||
              gdprDialog.confirmName !== (gdprDialog.user?.name ||
                gdprDialog.user?.email)
            }
            startIcon={gdprDialog.deleting
              ? <CircularProgress size={16} color="inherit" />
              : <GppMaybeIcon />}
            onClick={handleGDPRDelete}>
            {gdprDialog.deleting ? 'Raderar...' : 'Radera permanent'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}